import { ShaderMaterial, BackSide, GLSL3, Vector2, Vector3, Vector4, type IUniform } from 'three';
import type { SharedUniforms } from './sharedUniforms';
import type { SkyStop } from '../types';
import type { LinearRGB } from '../../core/color';
import { STYLE_CONSTANTS } from '../painterProjection';
import { createSkyShading, SKY_LOOK, PAINTER_WINDOW, type SkyShading } from '../skyModel';
import {
  WINDOW_BAND_START_DEG,
  WINDOW_BAND_STEP_DEG,
  WINDOW_KNOT_START_DEG,
  WINDOW_KNOT_STEP_DEG,
  type BandValues,
} from '../skyWindow';

import skyVert from '../shaders/sky.vert?raw';
import skyFrag from '../shaders/sky.frag?raw';

const DEG = Math.PI / 180;

function vec3Of(c: readonly [number, number, number]): Vector3 {
  return new Vector3(c[0], c[1], c[2]);
}

function vec4Of(v: BandValues): Vector4 {
  return new Vector4(v[0], v[1], v[2], v[3]);
}

function stopsFromShared(shared: SharedUniforms): readonly SkyStop[] {
  const count = shared.uSkyStopCount.value;
  return shared.uSkyStops.value.slice(0, count).map((c, i) => ({
    elevationDeg: shared.uSkyElevations.value[i]!,
    color: [c.x, c.y, c.z] as LinearRGB,
  }));
}

/* The material factory hands the sky only the shared uniforms, so the painter frame is rebuilt
   from the painter projection values they carry. */
export function skyShadingFromShared(shared: SharedUniforms): SkyShading {
  const f = shared.uPainterForwardDir.value;
  const r = shared.uPainterRightDir.value;
  return createSkyShading(stopsFromShared(shared), {
    forwardDir: [f.x, f.y, f.z],
    rightDir: [r.x, r.y, r.z],
    horizonDepressionRad: shared.uPainterHorizonDepressionRad.value,
    horizonElevationTop: shared.uPainterHorizonElevationTop.value,
    paintedHorizonFraction: STYLE_CONSTANTS.paintedHorizonRow / STYLE_CONSTANTS.imageHeight,
  });
}

export function createSkyUniforms(shading: SkyShading): Record<string, IUniform> {
  const darkness = shading.windowDarkness;
  const packedDarkness: Vector4[] = [];
  for (let i = 0; i < darkness.length; i += 4) {
    packedDarkness.push(
      new Vector4(darkness[i], darkness[i + 1], darkness[i + 2], darkness[i + 3])
    );
  }
  return {
    uSkyOvercast: { value: shading.overcastColors.map(vec3Of) },
    uSkyCloudDark: { value: vec3Of(shading.cloudDark) },
    uSkyMassScale: { value: vec3Of(shading.massScale) },
    uSkyStreakScale: { value: vec3Of(shading.streakScale) },
    uSkyWarpScale: { value: vec3Of(shading.warpScale) },
    uSkyWarpAmplitude: { value: shading.warpAmplitude },
    uSkyFrontScale: { value: vec3Of(shading.frontScale) },
    uSkyFrontAmplitude: { value: shading.frontAmplitude },
    uSkyFrontReach: { value: shading.frontReach },
    uSkyOverhead: { value: new Vector2(shading.overheadDeg[0], shading.overheadDeg[1]) },
    uSkySeed: { value: shading.seed },
    uSkyOutsideThreshold: { value: shading.outsideThreshold },
    uSkyOutsideSoftness: { value: shading.outsideSoftness },
    uSkyFree: { value: new Vector2(shading.freeKnotBlur, shading.freeEdgeSoftness) },
    uSkyWinThreshold: { value: shading.windowThresholds.map(vec4Of) },
    uSkyWinSoftness: { value: shading.windowSoftness.map(vec4Of) },
    uSkyWinDarkness: { value: packedDarkness },
    uSkyGrid: {
      value: new Vector4(
        WINDOW_KNOT_START_DEG,
        WINDOW_KNOT_STEP_DEG,
        WINDOW_BAND_START_DEG,
        WINDOW_BAND_STEP_DEG
      ),
    },
    uSkyLook: {
      value: new Vector3(SKY_LOOK.streakEdge, SKY_LOOK.streakCloud, SKY_LOOK.streakClear),
    },
    uSkyDarkRange: { value: new Vector2(SKY_LOOK.darkFrom, SKY_LOOK.darkTo) },
    uSkyFrameForward: { value: vec3Of(shading.frame.forward) },
    uSkyFrameUp: { value: vec3Of(shading.frame.up) },
    uSkyTanHorizon: { value: shading.frame.tanHorizon },
    uSkyTanTop: { value: shading.frame.tanTop },
    uSkyYaw: { value: shading.frame.yawBearing },
    uSkyWindow: {
      value: new Vector3(
        PAINTER_WINDOW.halfWidthDeg * DEG,
        PAINTER_WINDOW.marginDeg * DEG,
        PAINTER_WINDOW.darkMarginDeg * DEG
      ),
    },
  };
}

export function createSkyMaterial(sharedUniforms: SharedUniforms): ShaderMaterial {
  const material = new ShaderMaterial({
    glslVersion: GLSL3,
    vertexShader: skyVert,
    fragmentShader: skyFrag,
    uniforms: { ...sharedUniforms, ...createSkyUniforms(skyShadingFromShared(sharedUniforms)) },
    side: BackSide,
    depthWrite: false,
    depthTest: true,
  });

  material.userData['ams'] = true;

  return material;
}
